/**
 * IndexedDB Quota Management.
 *
 * Problem: IndexedDB quotas vary by browser (~50-100MB Chrome, 10MB Safari).
 * Without management, a runaway queue can exhaust storage and silently fail
 * subsequent writes — or worse, corrupt other site data.
 *
 * Solution: Two-tier cap.
 *   - Soft cap (warning): 500 entries. Log warning, allow write.
 *   - Hard cap (block):   1000 entries. Reject new writes with QuotaExceededError.
 *
 * Plus: Storage API proactive check (navigator.storage.estimate) when available.
 * Used by offlineQueueDB.enqueue() as gate.
 *
 * No data-loss strategy: we BLOCK new writes when full. The user sees a
 * clear error rather than silent data drops (which would be an anti-pattern
 * per OFFLINE_PATTERNS.md Section 9).
 */

import { getDepth as offlineGetDepth } from './offlineQueueDB.js';

const SOFT_CAP = 500;
const HARD_CAP = 1000;
const STORAGE_WARNING_THRESHOLD_PCT = 80; // warn when storage usage >= 80%
const STORAGE_BLOCK_THRESHOLD_PCT = 95;   // block when storage usage >= 95%

/**
 * Custom error for quota-exceeded condition.
 * Distinct from native QuotaExceededError (which is for actual IDB quota).
 */
export class QueueFullError extends Error {
  constructor(message, info) {
    super(message);
    this.name = 'QueueFullError';
    this.info = info; // { currentDepth, hardCap, reason }
  }
}

/**
 * Check storage usage via Storage API if available.
 *
 * @returns {Promise<{available: boolean, usagePct?: number, usage?: number, quota?: number}>}
 */
export async function getStorageEstimate() {
  if (typeof navigator === 'undefined' || !navigator.storage || !navigator.storage.estimate) {
    return { available: false };
  }
  try {
    const est = await navigator.storage.estimate();
    if (typeof est.usage !== 'number' || typeof est.quota !== 'number' || est.quota === 0) {
      return { available: false };
    }
    return {
      available: true,
      usage: est.usage,
      quota: est.quota,
      usagePct: (est.usage / est.quota) * 100,
    };
  } catch {
    return { available: false };
  }
}

/**
 * Check if queue depth is within bounds. Returns status + reason.
 *
 * @returns {Promise<{status: 'ok' | 'warning' | 'blocked', depth: number, reason?: string}>}
 */
export async function checkQuota() {
  const depth = await offlineGetDepth();
  const storage = await getStorageEstimate();

  // Hard storage check (Storage API)
  if (storage.available && storage.usagePct >= STORAGE_BLOCK_THRESHOLD_PCT) {
    return {
      status: 'blocked',
      depth,
      reason: `Browser storage usage ${storage.usagePct.toFixed(1)}% exceeds block threshold (${STORAGE_BLOCK_THRESHOLD_PCT}%)`,
      storage,
    };
  }

  // Hard queue cap
  if (depth >= HARD_CAP) {
    return {
      status: 'blocked',
      depth,
      reason: `Queue depth ${depth} exceeds hard cap (${HARD_CAP})`,
    };
  }

  // Soft thresholds (warnings)
  if (storage.available && storage.usagePct >= STORAGE_WARNING_THRESHOLD_PCT) {
    return {
      status: 'warning',
      depth,
      reason: `Browser storage usage ${storage.usagePct.toFixed(1)}% approaching block threshold`,
      storage,
    };
  }
  if (depth >= SOFT_CAP) {
    return {
      status: 'warning',
      depth,
      reason: `Queue depth ${depth} exceeds soft cap (${SOFT_CAP}); sync recommended`,
    };
  }

  return { status: 'ok', depth };
}

/**
 * Assert that the queue has room for a new entry. Throws QueueFullError if blocked.
 *
 * Designed to be called by offlineQueueDB.enqueue() before writing.
 *
 * @throws {QueueFullError}
 */
export async function assertHasCapacity() {
  const result = await checkQuota();
  if (result.status === 'blocked') {
    throw new QueueFullError(
      `Queue is full: ${result.reason}. New writes are blocked until existing entries sync.`,
      { currentDepth: result.depth, hardCap: HARD_CAP, reason: result.reason },
    );
  }
  if (result.status === 'warning') {
    console.warn(`[queueQuota] ${result.reason}`);
  }
}

// Test-only constants export
export const __testConstants = {
  SOFT_CAP,
  HARD_CAP,
  STORAGE_WARNING_THRESHOLD_PCT,
  STORAGE_BLOCK_THRESHOLD_PCT,
};
